import { format } from 'date-fns';
import {
  MINUTES_PER_HOUR,
  TIME_FORMAT_24H,
  TIME_FORMAT_12H,
  DATETIME_FORMAT_DISPLAY
} from './time-constants';

/**
 * Unified time formatting utilities
 * Consolidates all time formatting functions across the application
 */

// ===== UI Date Formatting =====

/**
 * Formats a date for UI display in standardized format (DD.MM.YYYY)
 * Uses British locale with dot separators for consistency across the entire application
 * @param date - Date object, ISO string, or timestamp
 * @returns Formatted date string (e.g., "15.01.2024")
 */
export const formatDateForUI = (date: Date | string | number): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-GB').replace(/\//g, '.');
};

// ===== Duration Formatting =====

/**
 * Formats duration in HH:MM format (00:00 to 999:59)
 * Consolidates formatHoursMinutes() from time-tracking-utils.ts
 */
export const formatDurationAsHours = (totalMinutes: number): string => {
  const hours = Math.floor(Math.abs(totalMinutes) / MINUTES_PER_HOUR);
  const minutes = Math.abs(totalMinutes) % MINUTES_PER_HOUR;
  const sign = totalMinutes < 0 ? '-' : '';
  
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Formats duration in human-readable format (2h 30m, 45m, etc.)
 * Consolidates formatShiftDuration() from shift-time-utils.ts
 */
export const formatDurationAsText = (minutes: number): string => {
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / MINUTES_PER_HOUR);
  const mins = absMinutes % MINUTES_PER_HOUR;
  const sign = minutes < 0 ? '-' : '';
  
  if (hours === 0) return `${sign}${mins}m`;
  if (mins === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h ${mins}m`;
};

/**
 * Formats duration with full words (2 hours 30 minutes)
 */
export const formatDurationVerbose = (minutes: number): string => {
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / MINUTES_PER_HOUR);
  const mins = absMinutes % MINUTES_PER_HOUR;
  const sign = minutes < 0 ? '-' : '';
  
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (mins > 0) {
    parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`);
  }
  
  return sign + (parts.length > 0 ? parts.join(' ') : '0 minutes');
};

// ===== Time Display Formatting =====

/**
 * Formats time with optional next-day indicator
 * Consolidates formatShiftTime() from shift-time-utils.ts and formatTimeWithDayIndicator() from shift-time-constraints.ts
 */
export const formatTimeWithDayIndicator = (
  time: Date, 
  isNextDay: boolean = false,
  use24Hour: boolean = false
): string => {
  const formatString = use24Hour ? TIME_FORMAT_24H : TIME_FORMAT_12H;
  const formatted = format(time, formatString);
  return isNextDay ? `${formatted} (+1)` : formatted;
};

/**
 * Formats time range (9:00 AM - 5:00 PM)
 */
export const formatTimeRange = (
  startTime: Date | string,
  endTime: Date | string,
  use24Hour: boolean = false,
  showNextDayIndicator: boolean = false
): string => {
  const startDate = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const endDate = typeof endTime === 'string' ? new Date(endTime) : endTime;
  
  const formatString = use24Hour ? TIME_FORMAT_24H : TIME_FORMAT_12H;
  const startFormatted = format(startDate, formatString);
  const endFormatted = format(endDate, formatString);
  
  const isNextDay = showNextDayIndicator && endDate.getDate() !== startDate.getDate();
  const endWithIndicator = isNextDay ? `${endFormatted} (+1)` : endFormatted;
  
  return `${startFormatted} - ${endWithIndicator}`;
};

/**
 * Formats business hours display text with overnight indication
 * Consolidates formatBusinessHoursDisplay() from calendar-utils.ts
 */
export const formatBusinessHoursDisplay = (
  openingTime: string, 
  closingTime: string,
  use24Hour: boolean = false
): string => {
  if (!openingTime || !closingTime) {
    return 'Hours not set';
  }

  const formatTime = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    
    return format(date, use24Hour ? TIME_FORMAT_24H : TIME_FORMAT_12H);
  };

  const openingDisplay = formatTime(openingTime);
  const closingDisplay = formatTime(closingTime);
  
  // Check if overnight (closing time is earlier than opening time)
  const [openHour, openMinute] = openingTime.split(':').map(Number);
  const [closeHour, closeMinute] = closingTime.split(':').map(Number);
  const openingMinutes = openHour * 60 + openMinute;
  const closingMinutes = closeHour * 60 + closeMinute;
  
  const isOvernight = closingMinutes < openingMinutes;
  
  return isOvernight 
    ? `${openingDisplay} - ${closingDisplay} (+1 day)`
    : `${openingDisplay} - ${closingDisplay}`;
};

// ===== Relative Time Formatting =====

/**
 * Formats time relative to now (5 minutes ago, in 2 hours)
 */
export const formatRelativeTime = (time: Date): string => {
  const now = new Date();
  const diffMinutes = Math.floor((time.getTime() - now.getTime()) / (1000 * 60));
  
  if (Math.abs(diffMinutes) < 1) {
    return 'Now';
  }
  
  if (diffMinutes > 0) {
    // Future
    if (diffMinutes < 60) {
      return `in ${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
  } else {
    // Past
    const absDiffMinutes = Math.abs(diffMinutes);
    if (absDiffMinutes < 60) {
      return `${absDiffMinutes} ${absDiffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      const hours = Math.floor(absDiffMinutes / 60);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
  }
};

// ===== Timestamp Formatting =====

/**
 * Formats full datetime for display
 */
export const formatDateTime = (time: Date, includeSeconds: boolean = false): string => {
  const formatString = includeSeconds ? 'MMM d, yyyy h:mm:ss a' : DATETIME_FORMAT_DISPLAY;
  return format(time, formatString);
};

/**
 * Formats time only (no date)
 */
export const formatTimeOnly = (time: Date, use24Hour: boolean = false): string => {
  return format(time, use24Hour ? TIME_FORMAT_24H : TIME_FORMAT_12H);
};

/**
 * Formats date only (no time)
 */
export const formatDateOnly = (date: Date): string => {
  return format(date, 'MMM d, yyyy');
};

// ===== Utility Functions =====

/**
 * Checks if two dates represent different days
 */
export const isDifferentDay = (date1: Date, date2: Date): boolean => {
  return date1.toDateString() !== date2.toDateString();
};

/**
 * Gets display text for day difference
 */
export const getDayDifferenceText = (startDate: Date, endDate: Date): string => {
  if (!isDifferentDay(startDate, endDate)) {
    return 'Same day';
  }
  
  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return 'Next day';
  if (diffDays === -1) return 'Previous day';
  return `${Math.abs(diffDays)} days ${diffDays > 0 ? 'later' : 'earlier'}`;
};